import { useEffect, useId, useRef, useState } from 'react';
import { Button } from './button';
import { Input } from './input';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  loading?: boolean;
  /** When set, user must type this exact phrase to enable Confirm. */
  confirmPhrase?: string;
  confirmPhraseLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  loading = false,
  confirmPhrase,
  confirmPhraseLabel,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const titleId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const phraseInputRef = useRef<HTMLInputElement>(null);
  const [typedPhrase, setTypedPhrase] = useState('');

  const phraseRequired = Boolean(confirmPhrase);
  const phraseMatches = !phraseRequired || typedPhrase.trim() === (confirmPhrase ?? '');

  useEffect(() => {
    if (!open) {
      setTypedPhrase('');
      return;
    }
    if (phraseRequired) {
      phraseInputRef.current?.focus();
    } else {
      confirmRef.current?.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, loading, onCancel, phraseRequired]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md rounded-lg border border-nova-border bg-nova-card p-4 text-nova-foreground shadow-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-base font-semibold">
          {title}
        </h2>
        <p className="mt-1.5 text-sm text-nova-muted-foreground">{message}</p>
        {confirmPhrase ? (
          <div className="mt-3">
            <Input
              ref={phraseInputRef}
              label={confirmPhraseLabel ?? `Type ${confirmPhrase} to confirm`}
              value={typedPhrase}
              autoComplete="off"
              spellCheck={false}
              disabled={loading}
              placeholder={confirmPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && phraseMatches && !loading) {
                  e.preventDefault();
                  onConfirm();
                }
              }}
            />
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" disabled={loading} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            type="button"
            variant={confirmVariant}
            size="sm"
            loading={loading}
            disabled={!phraseMatches}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
